import { useEffect, useState } from "react";
import { campaignService } from "../../services/campaignService";
import Icon from "../../components/Common/Icon";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
const fmtDiscount = (c) =>
  c.discountType === "PERCENTAGE"
    ? `%${Number(c.discountValue)}`
    : `${Number(c.discountValue).toLocaleString("tr-TR")} ₺`;

const todayStr = () => new Date().toISOString().slice(0, 10);
const emptyForm = {
  name: "",
  description: "",
  discountType: "PERCENTAGE",
  discountValue: "",
  code: "",
  startDate: todayStr(),
  endDate: todayStr(),
  minPurchase: "",
  usageLimit: "",
  active: true,
};

export default function CampaignsPage() {
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  const refresh = () => {
    setLoading(true);
    campaignService
      .list()
      .then((res) => setItems(res.data?.data ?? []))
      .catch(() => showToast("Kampanyalar yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name ?? "",
      description: row.description ?? "",
      discountType: row.discountType ?? "PERCENTAGE",
      discountValue: row.discountValue ?? "",
      code: row.code ?? "",
      startDate: row.startDate ?? todayStr(),
      endDate: row.endDate ?? todayStr(),
      minPurchase: row.minPurchase ?? "",
      usageLimit: row.usageLimit ?? "",
      active: row.active,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim()) return setFormError("Kampanya adı zorunludur.");
    if (!form.discountValue || Number(form.discountValue) <= 0)
      return setFormError("İndirim değeri pozitif olmalıdır.");
    if (new Date(form.endDate) < new Date(form.startDate))
      return setFormError("Bitiş tarihi başlangıçtan önce olamaz.");

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      code: form.code.trim() || null,
      startDate: form.startDate,
      endDate: form.endDate,
      minPurchase: form.minPurchase ? Number(form.minPurchase) : null,
      usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
      active: form.active,
    };
    setSaving(true);
    try {
      if (editingId) {
        await campaignService.update(editingId, payload);
        showToast("Kampanya güncellendi.", "success");
      } else {
        await campaignService.create(payload);
        showToast("Kampanya oluşturuldu.", "success");
      }
      setModalOpen(false);
      refresh();
    } catch (err) {
      setFormError(err?.response?.data?.message || "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await campaignService.setActive(row.id, !row.active);
      refresh();
    } catch {
      showToast("Durum değiştirilemedi.", "error");
    }
  };

  const triggerDelete = (row) => {
    setConfirmModal({
      isOpen: true,
      title: "Kampanyayı sil",
      message: `"${row.name}" kampanyası kalıcı olarak silinsin mi?`,
      type: "danger",
      confirmText: "Evet, sil",
      onConfirm: async () => {
        try {
          await campaignService.remove(row.id);
          showToast("Kampanya silindi.", "success");
          refresh();
        } catch {
          showToast("Kampanya silinemedi.", "error");
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="local_offer" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Kampanyalar</h1>
            <p className="text-on-surface-variant text-sm">İndirim kampanyalarını oluştur ve yönet.</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition"
        >
          <Icon name="add" className="!text-[20px]" /> Yeni Kampanya
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="bg-surface-card rounded-2xl p-10 border border-outline-variant text-center text-on-surface-variant">
          Yükleniyor…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-surface-card rounded-2xl p-10 border border-outline-variant text-center text-on-surface-variant">
          Henüz kampanya yok. “Yeni Kampanya” ile ekleyin.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((c) => (
            <div key={c.id} className="bg-surface-card rounded-2xl border border-outline-variant p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-bold text-on-surface truncate">{c.name}</h3>
                  {c.code && (
                    <span className="inline-block mt-1 text-[11px] font-mono bg-surface-container-high text-on-surface-variant px-2 py-0.5 rounded">
                      {c.code}
                    </span>
                  )}
                </div>
                <span
                  className={`shrink-0 text-[11px] font-semibold px-2 py-1 rounded-full ${
                    c.currentlyActive
                      ? "bg-success/10 text-success"
                      : c.active
                      ? "bg-tier-gold/10 text-tier-gold"
                      : "bg-surface-container-high text-on-surface-variant"
                  }`}
                >
                  {c.currentlyActive ? "Geçerli" : c.active ? "Zamanlı" : "Pasif"}
                </span>
              </div>

              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-primary">{fmtDiscount(c)}</span>
                <span className="text-xs text-on-surface-variant">indirim</span>
              </div>

              {c.description && (
                <p className="text-xs text-on-surface-variant line-clamp-2">{c.description}</p>
              )}

              <div className="text-xs text-on-surface-variant space-y-1">
                <div className="flex items-center gap-1.5">
                  <Icon name="event" className="!text-[16px]" />
                  {fmtDate(c.startDate)} — {fmtDate(c.endDate)}
                </div>
                {c.usageLimit != null && (
                  <div className="flex items-center gap-1.5">
                    <Icon name="confirmation_number" className="!text-[16px]" />
                    {c.timesUsed}/{c.usageLimit} kullanım
                  </div>
                )}
                {c.minPurchase != null && (
                  <div className="flex items-center gap-1.5">
                    <Icon name="shopping_cart" className="!text-[16px]" />
                    Min. {Number(c.minPurchase).toLocaleString("tr-TR")} ₺
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1 pt-2 border-t border-outline-variant">
                <button
                  onClick={() => openEdit(c)}
                  className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-on-surface hover:text-primary py-1.5 rounded-lg hover:bg-surface-container-high transition"
                >
                  <Icon name="edit" className="!text-[18px]" /> Düzenle
                </button>
                <button
                  onClick={() => toggleActive(c)}
                  className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-on-surface hover:text-primary py-1.5 rounded-lg hover:bg-surface-container-high transition"
                >
                  <Icon name={c.active ? "toggle_on" : "toggle_off"} className="!text-[18px]" />
                  {c.active ? "Pasife al" : "Aktifleştir"}
                </button>
                <button
                  onClick={() => triggerDelete(c)}
                  className="flex items-center justify-center text-xs font-semibold text-error hover:bg-error/5 py-1.5 px-2 rounded-lg transition"
                  title="Sil"
                >
                  <Icon name="delete" className="!text-[18px]" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ekle/Düzenle modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4 z-[999] animate-fade-in">
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl shadow-modal border border-outline-variant max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto animate-scale-up"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg text-on-surface">
                {editingId ? "Kampanyayı Düzenle" : "Yeni Kampanya"}
              </h3>
              <button type="button" onClick={() => setModalOpen(false)} className="p-1 text-on-surface-variant hover:text-error transition">
                <Icon name="close" />
              </button>
            </div>

            {formError && (
              <div className="bg-error/10 text-error text-sm px-3 py-2 rounded-xl">{formError}</div>
            )}

            <Field label="Kampanya Adı *">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="inp" placeholder="Örn. Yaz İndirimi" />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="İndirim Türü *">
                <select value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })} className="inp">
                  <option value="PERCENTAGE">Yüzde (%)</option>
                  <option value="FIXED">Sabit Tutar (₺)</option>
                </select>
              </Field>
              <Field label={form.discountType === "PERCENTAGE" ? "İndirim (%) *" : "İndirim (₺) *"}>
                <input type="number" min="0" step="any" value={form.discountValue}
                  onChange={(e) => setForm({ ...form, discountValue: e.target.value })} className="inp" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Başlangıç *">
                <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="inp" />
              </Field>
              <Field label="Bitiş *">
                <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="inp" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Kod (opsiyonel)">
                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="inp" placeholder="YAZ25" />
              </Field>
              <Field label="Kullanım Limiti">
                <input type="number" min="1" value={form.usageLimit}
                  onChange={(e) => setForm({ ...form, usageLimit: e.target.value })} className="inp" placeholder="Sınırsız" />
              </Field>
            </div>

            <Field label="Min. Harcama (₺, opsiyonel)">
              <input type="number" min="0" step="any" value={form.minPurchase}
                onChange={(e) => setForm({ ...form, minPurchase: e.target.value })} className="inp" />
            </Field>

            <Field label="Açıklama">
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} className="inp resize-none" />
            </Field>

            <label className="flex items-center gap-2 text-sm text-on-surface">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Aktif
            </label>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-on-surface border border-outline-variant rounded-xl hover:bg-surface-container-high transition">
                Vazgeç
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-xs font-semibold bg-primary text-white rounded-xl hover:opacity-90 transition disabled:opacity-50">
                {saving ? "Kaydediliyor…" : "Kaydet"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmationModal {...confirmModal} onClose={() => setConfirmModal({ isOpen: false })} />

      <style>{`
        .inp { width:100%; background:rgb(var(--c-surface-card)); color:rgb(var(--c-on-surface));
          border:1px solid rgb(var(--c-outline-variant));
          border-radius:0.75rem; padding:0.5rem 0.75rem; font-size:0.875rem; outline:none; }
        .inp:focus { border-color: rgb(var(--c-primary)); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1 text-on-surface-variant">{label}</label>
      {children}
    </div>
  );
}
